import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  Skeleton,
  Stack,
  InputAdornment,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Apple, Copy, Eye, EyeOff, Gamepad2 } from "lucide-react";
import TrackInfoTool from "../../tools/TrackInfoTool";
import { Message } from "../../messages/Message";
import AdminService from "../../../services/AdminService";
import {
  AssetType,
  BrandedAppStatus,
  IAddServiceClientCommand,
  IUpdateBrandedAppLogoCommand,
  LogoType,
} from "@growth-machine-llc/stridist-api-client";
import { COACH_MESSAGE_ROUTE } from "../../../routes/routes";
import { ADMIN_BRANDED_APPS_QUERY_KEY } from "./BrandedAppsTable";
import { ImageCropDialog } from "../../new-editor/elements/ImageCropDialog";
import StorageService from "../../../services/StorageService";
import { uploadFile } from "../../../utils/file";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 800,
    margin: "0 auto",
  },
  backButton: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(5),
  },
  avatar: {
    width: 48,
    height: 48,
    marginRight: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(5),
  },
  imagesContainer: {
    display: "flex",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  imageBox: {
    textAlign: "center",
    width: 120,
    "& img": {
      width: 120,
      height: 120,
      objectFit: "cover",
      borderRadius: theme.shape.borderRadius,
      display: "block",
      marginBottom: theme.spacing(1),
    },
    "& > .MuiTypography-root": {
      marginBottom: theme.spacing(1),
    },
  },
  formSection: {
    marginBottom: theme.spacing(5),
  },
  iconLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

export const ADMIN_BRANDED_APP_INFO_QUERY_KEY = "admin-branded-app-info";

export function BrandedAppPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToastAlert } = useToastAlert();
  const { workspaceId: workspaceIdParam } = useParams();
  const workspaceId = Number(workspaceIdParam);
  const queryClient = useQueryClient();

  const { data: vm, isLoading } = useQuery({
    queryKey: [ADMIN_BRANDED_APP_INFO_QUERY_KEY, { workspaceId }],
    queryFn: () => AdminService.getBrandedApp(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const { mutate: createUrl } = useMutation({
    mutationFn: StorageService.createSignedUrl,
  });
  const [appleUrl, setAppleUrl] = useState("");
  const [playUrl, setPlayUrl] = useState("");
  const [brandMark, setBrandMark] = useState(false);
  const [appleMark, setAppleMark] = useState(false);
  const [playMark, setPlayMark] = useState(false);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSuccess, setStatusSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Cropper dialog state
  const [logoCropperOpen, setLogoCropperOpen] = useState(false);
  const [bannerCropperOpen, setBannerCropperOpen] = useState(false);
  const [splashScreenCropperOpen, setSplashScreenCropperOpen] = useState(false);

  // Mutation to update logo/banner
  const { mutate: updateLogo, isPending: updateLogoLoading } = useMutation({
    mutationFn: (variables: IUpdateBrandedAppLogoCommand) =>
      AdminService.updateBrandedAppLogo(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BRANDED_APPS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BRANDED_APP_INFO_QUERY_KEY],
      });
    },
    onError: (err: any) => {
      console.error("Error updating logo/banner:", err);
    },
  });

  const {
    mutate: addServiceClientMutation,
    isPending: addServiceClientPending,
  } = useMutation({
    mutationFn: (variables: IAddServiceClientCommand) =>
      AdminService.addServiceClient(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BRANDED_APP_INFO_QUERY_KEY, { workspaceId }],
      });
    },
    onError: (err: any) => {
      console.error("Failed to register service client:", err);
    },
  });

  // Helper to handle file upload then mutation
  const handleImageSave = (onSuccess: (url: string) => void) => {
    return (file: File) => {
      createUrl(
        {
          refId: workspaceId,
          refType: "Workspace",
          file: file.name,
          assetType: AssetType.BRANDED_APP,
        },
        {
          onSuccess: (urlVm) => {
            const signedUrl = urlVm.url;
            uploadFile(signedUrl, file)
              .then(() => {
                const urlObj = new URL(
                  `https:/${signedUrl.substr(signedUrl.indexOf("/", 8))}`,
                );
                urlObj.search = "";
                onSuccess(String(urlObj));
              })
              .catch((error) => console.error("Upload error:", error));
          },
        },
      );
    };
  };

  useEffect(() => {
    if (vm?.app) {
      document.title = vm.app.name || "";
      setAppleUrl(vm.app.appleStoreUrl || "");
      setPlayUrl(vm.app.googlePlayUrl || "");
      setBrandMark(!!vm.app.brandCompletedMark);
      setAppleMark(!!vm.app.appleStoreAccessMark);
      setPlayMark(!!vm.app.googlePlayAccessMark);
      setStatus(vm.app.status.toString() || "");
    }
  }, [vm]);

  const { mutate: updateMutation, isPending: updateMutationPending } =
    useMutation({
      mutationFn: AdminService.updateBrandedApp,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [ADMIN_BRANDED_APPS_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [ADMIN_BRANDED_APP_INFO_QUERY_KEY],
        });
      },
    });

  const { mutate: statusMutation, isPending: statusMutationPending } =
    useMutation({
      mutationFn: AdminService.changeBrandedAppStatus,
      onSuccess: (res) => {
        if (res.success) {
          setStatusSuccess(res.message || "Status updated");
          setStatusError(null);
          queryClient.invalidateQueries({
            queryKey: [ADMIN_BRANDED_APPS_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [ADMIN_BRANDED_APP_INFO_QUERY_KEY],
          });
        } else {
          setStatusError(res.message || "Failed to update status");
        }
      },
      onError: (err: any) =>
        setStatusError(err.message || "Failed to update status"),
    });

  const handleRegisterServiceClient = () => {
    if (vm?.app.id) {
      addServiceClientMutation({ appId: vm.app.id });
    }
  };

  const copyToClipboard = async (text: string | undefined) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch (err) {
      showToastAlert("error", {
        message: "Failed to copy to clipboard",
      });
    }
  };

  const handleSave = () =>
    updateMutation({
      appId: vm?.app.id,
      appleStoreUrl: appleUrl,
      googlePlayUrl: playUrl,
      brandCompletedMark: brandMark,
      appleStoreAccessMark: appleMark,
      googlePlayAccessMark: playMark,
    });

  const handleStatusSave = () =>
    statusMutation({ appId: vm?.app.id, status: status as BrandedAppStatus });

  const startOrGoToChat = React.useCallback(() => {
    const username = vm?.app.coach?.username;
    if (!username) return;
    const chatUrl = COACH_MESSAGE_ROUTE.replace(":recipient", username);
    window.open(chatUrl, "_blank");
  }, [vm?.app.coach?.username]);

  if (isLoading || !vm) {
    return <Skeleton variant="rectangular" width="100%" height={300} />;
  }

  const { app, canChat, lastMessages } = vm;

  return (
    <Box className={classes.root}>
      <Button
        className={classes.backButton}
        variant="text"
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </Button>

      <Box className={classes.header}>
        <Avatar src={app.coach?.photoUrl} className={classes.avatar} />
        <Box>
          <Typography variant="h4">{app.name}</Typography>
          <Typography color="textSecondary">
            {app.coach?.displayName} ({app.coach?.email})
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Workspace ID: {workspaceId}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.section}>
        <Typography color="textPrimary" sx={{ mb: 0.5 }}>
          <b>Status:</b> {app.status}
        </Typography>
        <Typography color="textPrimary">
          <b>App name:</b> {app.name}
        </Typography>
      </Box>

      <Box className={classes.formSection}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Service Client Account
        </Typography>
        {app.serviceClientEmail ? (
          <Stack spacing={2}>
            <TextField
              label="Service Client Email"
              fullWidth
              value={app.serviceClientEmail || ""}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="copy email"
                        onClick={() => copyToClipboard(app.serviceClientEmail)}
                      >
                        <Copy size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Service Client Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={app.serviceClientPassword || ""}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </IconButton>
                      <IconButton
                        aria-label="copy password"
                        onClick={() =>
                          copyToClipboard(app.serviceClientPassword)
                        }
                      >
                        <Copy size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        ) : (
          <Button
            variant="contained"
            onClick={handleRegisterServiceClient}
            disabled={addServiceClientPending}
          >
            {addServiceClientPending ? "Registering..." : "Register"}
          </Button>
        )}
      </Box>

      <Box className={classes.section}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Images
        </Typography>
        <Box className={classes.imagesContainer}>
          <Box className={classes.imageBox}>
            {app.logoUrl ? (
              <>
                <img src={app.logoUrl} alt="Logo" />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => window.open(app.logoUrl, "_blank")}
                >
                  Open
                </Button>
                <Button
                  variant="text"
                  onClick={() => setLogoCropperOpen(true)}
                  disabled={updateLogoLoading}
                >
                  Upload
                </Button>
              </>
            ) : (
              <>
                <Typography>No logo uploaded yet</Typography>
                <Button variant="text" onClick={() => setLogoCropperOpen(true)}>
                  Upload
                </Button>
              </>
            )}
          </Box>
          <Box className={classes.imageBox}>
            {app.bannerUrl ? (
              <>
                <img src={app.bannerUrl} alt="Banner" />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => window.open(app.bannerUrl, "_blank")}
                >
                  Open
                </Button>
                <Button
                  variant="text"
                  onClick={() => setBannerCropperOpen(true)}
                  disabled={updateLogoLoading}
                >
                  Upload
                </Button>
              </>
            ) : (
              <>
                <Typography>No banner uploaded yet</Typography>
                <Button
                  variant="text"
                  onClick={() => setBannerCropperOpen(true)}
                >
                  Upload
                </Button>
              </>
            )}
          </Box>
          <Box className={classes.imageBox}>
            {app.splashScreenUrl ? (
              <>
                <img src={app.splashScreenUrl} alt="Splash Screen" />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => window.open(app.splashScreenUrl, "_blank")}
                >
                  Open
                </Button>
                <Button
                  variant="text"
                  onClick={() => setSplashScreenCropperOpen(true)}
                  disabled={updateLogoLoading}
                >
                  Upload
                </Button>
              </>
            ) : (
              <>
                <Typography>No splash screen uploaded yet</Typography>
                <Button
                  variant="text"
                  onClick={() => setSplashScreenCropperOpen(true)}
                >
                  Upload
                </Button>
              </>
            )}
          </Box>
          <Box className={classes.imageBox}>
            <Box
              sx={{
                width: 120,
                height: 120,
                backgroundColor: app.splashBackgroundColor || "#FFFFFF",
                border: "1px solid #ccc",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color:
                    app.splashBackgroundColor?.toUpperCase() === "#FFFFFF" ||
                    !app.splashBackgroundColor
                      ? "#000"
                      : "#FFF",
                  textShadow: "0 0 2px rgba(0,0,0,0.3)",
                }}
              >
                {app.splashBackgroundColor || "#FFFFFF"}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Splash Background
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.formSection}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Store Links & Marks
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Apple Store URL"
            fullWidth
            value={appleUrl}
            onChange={(e) => setAppleUrl(e.target.value)}
          />
          <TextField
            label="Google Play URL"
            fullWidth
            value={playUrl}
            onChange={(e) => setPlayUrl(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={brandMark}
                onChange={(e) => setBrandMark(e.target.checked)}
              />
            }
            label="Brand Completed"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={appleMark}
                onChange={(e) => setAppleMark(e.target.checked)}
              />
            }
            label={
              <Box className={classes.iconLabel}>
                <Apple size={16} />
                <Typography>App Store Access</Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={playMark}
                onChange={(e) => setPlayMark(e.target.checked)}
              />
            }
            label={
              <Box className={classes.iconLabel}>
                <Gamepad2 size={16} />
                <Typography>Play Store Access</Typography>
              </Box>
            }
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateMutationPending}
          >
            Save Changes
          </Button>
        </Stack>
      </Box>

      <Box className={classes.formSection}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Change Status
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {Object.values(BrandedAppStatus).map((st) => (
                <MenuItem key={st} value={st}>
                  {st}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={handleStatusSave}
            disabled={statusMutationPending}
          >
            Update Status
          </Button>
          {statusSuccess && <Alert severity="success">{statusSuccess}</Alert>}
          {statusError && <Alert severity="error">{statusError}</Alert>}
        </Stack>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Messages
        </Typography>
        {lastMessages && lastMessages.length > 0 ? (
          lastMessages.map((msg, idx) => (
            <Message
              key={msg.id || idx}
              message={msg}
              displayDate
              displayUserName
              displayAvatar
            />
          ))
        ) : canChat ? (
          <Button variant="text" onClick={startOrGoToChat}>
            Start Conversation
          </Button>
        ) : (
          <Typography>No messages</Typography>
        )}
        {lastMessages && lastMessages.length > 0 && canChat && (
          <Button onClick={startOrGoToChat}>Go to Chat</Button>
        )}
      </Box>

      <TrackInfoTool
        trackInfo={{
          name: `Admin - BrandedApp - ${app.name} (${app.coach?.displayName})`,
        }}
      />

      {/* Image croppers */}
      <ImageCropDialog
        imageRestrictions={{ aspectRatio: 1 / 1, minWidth: 1024 }}
        open={logoCropperOpen}
        onClose={() => setLogoCropperOpen(false)}
        onSubmit={(file) => {
          handleImageSave((url) =>
            updateLogo({ appId: app.id, url, type: LogoType.Logo }),
          )(file);
          setLogoCropperOpen(false);
        }}
      />
      <ImageCropDialog
        imageRestrictions={{
          aspectRatio: { maximum: 10 / 1, minimum: 1 },
          minHeight: 50,
        }}
        open={bannerCropperOpen}
        onClose={() => setBannerCropperOpen(false)}
        onSubmit={(file) => {
          handleImageSave((url) =>
            updateLogo({ appId: app.id, url, type: LogoType.Banner }),
          )(file);
          setBannerCropperOpen(false);
        }}
      />
      <ImageCropDialog
        imageRestrictions={{ aspectRatio: 1 / 1, minWidth: 2732 }}
        open={splashScreenCropperOpen}
        onClose={() => setSplashScreenCropperOpen(false)}
        onSubmit={(file) => {
          handleImageSave((url) =>
            updateLogo({ appId: app.id, url, type: LogoType.SplashScreen }),
          )(file);
          setSplashScreenCropperOpen(false);
        }}
      />
    </Box>
  );
}
